import React, { useState } from 'react';
import FriendFilterDropdown from './FriendFilterDropdown';
import { isParty, filterOneToOneExpenses } from '../../utils/expenseUtils';

function Dashboard({ balances, users, expenses, user, onSettleUp, onDeleteExpense }) {
  const [friendFilter, setFriendFilter] = useState('');
  const getFriendName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? u.name : 'Unknown';
  };

  const getCategoryEmoji = (desc) => {
    const d = desc.toLowerCase();
    if (d.includes('food') || d.includes('dinner') || d.includes('lunch') || d.includes('eat') || d.includes('drink')) return '🍔';
    if (d.includes('travel') || d.includes('cab') || d.includes('flight') || d.includes('uber') || d.includes('fuel')) return '🚗';
    if (d.includes('rent') || d.includes('flat') || d.includes('stay') || d.includes('hotel')) return '🏠';
    if (d.includes('bill') || d.includes('electricity') || d.includes('wifi') || d.includes('water')) return '💡';
    if (d.includes('settle') || d.includes('payment') || d.includes('paid')) return '🤝';
    return '📦';
  };

  // Filter out users who have zero balance to keep dashboard clean, but show all if needed
  const nonZeroBalances = balances.balancesPerUser.filter((b) => Math.abs(b.balance) > 0.01);

  return (
    <div className="dashboard-grid">
      {/* 3 Overview Cards */}
      <section className="overview-cards">
        <div className="stat-card">
          <div className="stat-label">Total Net Balance</div>
          <div className={`stat-value ${balances.netBalance >= 0 ? 'positive' : 'negative'}`}>
            {balances.netBalance >= 0 ? `+₹${balances.netBalance.toFixed(2)}` : `-₹${Math.abs(balances.netBalance).toFixed(2)}`}
          </div>
          <div className="stat-indicator">aggregated status</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">You owe overall</div>
          <div className="stat-value negative">₹{balances.totalOwedToOthers.toFixed(2)}</div>
          <div className="stat-indicator">to settle up</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">You are owed overall</div>
          <div className="stat-value positive">₹{balances.totalOwedFromOthers.toFixed(2)}</div>
          <div className="stat-indicator">to receive</div>
        </div>
      </section>

      {/* Friends Balance Grid & Recent Transactions */}
      <div className="dashboard-details">
        {/* Friends Balance Section */}
        <section className="dashboard-section card-box">
          <h3>👥 Balances with Friends</h3>
          {nonZeroBalances.length === 0 ? (
            <div className="empty-state">
              <p>All settled up! No active debts with friends. 🎉</p>
            </div>
          ) : (
            <div className="friend-balance-list">
              {nonZeroBalances.map((friend) => (
                <div key={friend.userId} className="friend-balance-row">
                  <div className="friend-avatar-circle">
                    {friend.userName.substring(0, 1).toUpperCase()}
                  </div>
                  <div className="friend-info">
                    <span className="friend-name">{friend.userName}</span>
                    <span className={`friend-status ${friend.balance >= 0 ? 'positive' : 'negative'}`}>
                      {friend.balance >= 0 ? 'owes you' : 'you owe'}
                    </span>
                  </div>
                  <div className={`friend-balance-val ${friend.balance >= 0 ? 'positive' : 'negative'}`}>
                    ₹{Math.abs(friend.balance).toFixed(2)}
                  </div>
                  <button
                    className="btn-tiny"
                    onClick={() => onSettleUp(friend)}
                  >
                    Settle
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recent Transactions Section */}
        <section className="dashboard-section card-box">
          <div className="container-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>⏳ Recent Expenses</h3>
            <FriendFilterDropdown users={users} currentUserId={user.id} value={friendFilter} onChange={setFriendFilter} />
          </div>
          {(() => {
            const visibleExpenses = friendFilter ? filterOneToOneExpenses(expenses, user.id, friendFilter) : expenses;
            if (visibleExpenses.length === 0) {
              return (
                <div className="empty-state">
                  <p>{friendFilter ? `No expenses shared with ${getFriendName(friendFilter)} yet.` : 'No expenses added yet.'}</p>
                </div>
              );
            }
            return (
              <div className="recent-expense-list">
                {visibleExpenses.slice(0, 5).map((expense) => {
                const paidByMe = expense.paidByUserId === user.id;
                const isSettlement = expense.description.toLowerCase().includes('settle');
                return (
                  <div key={expense.id} className="recent-expense-row">
                    <div className="expense-emoji">{getCategoryEmoji(expense.description)}</div>
                    <div className="expense-details-col">
                      <span className="expense-desc">{expense.description}</span>
                      <span className="expense-meta">
                        Paid by {paidByMe ? 'You' : getFriendName(expense.paidByUserId)}
                      </span>
                    </div>
                    <div className="expense-amount-col">
                      <span className="expense-val">₹{expense.amount.toFixed(2)}</span>
                      <span className={`expense-relationship ${paidByMe ? 'positive' : 'negative'}`}>
                        {isSettlement 
                          ? 'settled'
                          : paidByMe 
                            ? 'you lent' 
                            : 'you borrowed'
                        }
                      </span>
                    </div>
                    {isParty(expense, user.id) ? (
                      <button className="btn-icon-delete" onClick={() => onDeleteExpense(expense.id)}>
                        🗑️
                      </button>
                    ) : (
                      <span className="btn-icon-delete" title="You can only delete expenses you paid or participated in." style={{cursor: 'help'}}>
                        🔒
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            );
          })()}
        </section>
      </div>
    </div>
  );
}

export default Dashboard;
